import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { useTheme } from '@lifeomic/chroma-react/styles';
import { useActiveProjectId, useServiceAuth } from '../../hooks';
import { getTestProps } from '../../util/testProps';
import {
  formatCollectionDate,
  MemberExtractionHistoryEntry,
  peekMemberExtractionValueHistory,
  requestMemberExtractionValueHistory,
  toHistoryEntries,
  valueHistoryFetchLimit,
} from './memberExtractionHistory';
import { MemberExtractionHistorySkeleton } from './MemberExtractionSkeletons';
import { heroSpace } from './memberExtractionTheme';
import type { ExtractedClinicalResource } from './useMemberExtractionDocuments';

const messages = defineMessages({
  empty: {
    id: 'phc-web-toolkit.memberExtractions.history.empty',
    defaultMessage: 'No earlier results',
  },
  error: {
    id: 'phc-web-toolkit.memberExtractions.history.error',
    defaultMessage: 'Unable to load history',
  },
  loadMore: {
    id: 'phc-web-toolkit.memberExtractions.history.loadMore',
    defaultMessage: 'load more...',
  },
  historicalValues: {
    id: 'phc-web-toolkit.memberExtractions.history.historicalValues',
    defaultMessage: 'historical values',
  },
});

const historyStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  rowGap: heroSpace.tight,
};

const historyRowStyle = {
  display: 'flex',
  columnGap: heroSpace.regular,
};

const historyGroupStyle = {
  display: 'flex',
  alignItems: 'stretch',
  columnGap: heroSpace.regular,
};

const braceLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  columnGap: heroSpace.tight,
  flex: '0 0 auto',
};

const braceSlotStyle = {
  position: 'relative' as const,
  width: 10,
  alignSelf: 'stretch',
};

const braceStyle = {
  position: 'absolute' as const,
  top: 0,
  bottom: 0,
  left: 0,
  width: 10,
  height: '100%',
};

const loadMoreStyle = {
  alignSelf: 'flex-start',
  margin: 0,
  padding: 0,
  border: 0,
  background: 'none',
  cursor: 'pointer',
  font: 'inherit',
};

type MemberExtractionValueHistoryProps = {
  patientId: string;
  system: string;
  code: string;
  excludeId?: string;
  visibleCount: number;
  historyRowClassName: string;
  onLoadMore: () => void;
};

export const MemberExtractionValueHistory: React.FC<
  MemberExtractionValueHistoryProps
> = ({
  patientId,
  system,
  code,
  excludeId,
  visibleCount,
  historyRowClassName,
  onLoadMore,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const auth = useServiceAuth();
  const projectId = useActiveProjectId();
  const cachedResources = projectId
    ? peekMemberExtractionValueHistory(
        projectId,
        patientId,
        system,
        code,
        valueHistoryFetchLimit(visibleCount),
      )
    : undefined;
  const cachedEntries = cachedResources
    ? toHistoryEntries(cachedResources, excludeId)
    : undefined;
  const [entries, setEntries] = React.useState<MemberExtractionHistoryEntry[]>(
    () => cachedEntries?.slice(0, visibleCount) ?? [],
  );
  const [hasMore, setHasMore] = React.useState(() =>
    cachedEntries ? cachedEntries.length > visibleCount : false,
  );
  const [isLoading, setIsLoading] = React.useState(!cachedResources);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    const account = auth?.account;
    const authToken = auth?.authToken;
    if (!account || !authToken || !projectId) {
      setEntries([]);
      setHasMore(false);
      setIsLoading(false);
      setFailed(false);
      return;
    }

    const showEntries = (resources: ExtractedClinicalResource[]) => {
      const nextEntries = toHistoryEntries(resources, excludeId);
      setHasMore(nextEntries.length > visibleCount);
      setEntries(nextEntries.slice(0, visibleCount));
      setIsLoading(false);
    };

    const cached = peekMemberExtractionValueHistory(
      projectId,
      patientId,
      system,
      code,
      valueHistoryFetchLimit(visibleCount),
    );
    if (cached) {
      setFailed(false);
      showEntries(cached);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setFailed(false);
    requestMemberExtractionValueHistory(
      auth,
      projectId,
      patientId,
      system,
      code,
      valueHistoryFetchLimit(visibleCount),
    )
      .then(resources => {
        if (!cancelled) {
          showEntries(resources);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [auth, code, excludeId, patientId, projectId, system, visibleCount]);

  let status: string | undefined;
  if (failed && entries.length === 0) {
    status = formatMessage(messages.error);
  } else if (!isLoading && entries.length === 0) {
    status = formatMessage(messages.empty);
  }

  return (
    <div
      style={historyStyle}
      {...getTestProps('member-extraction-value-history')}
    >
      {status ? <Text size="caption">{status}</Text> : null}
      {isLoading && entries.length === 0 ? (
        <MemberExtractionHistorySkeleton />
      ) : null}
      {entries.length > 0 ? (
        <div style={historyGroupStyle}>
          <div style={historyStyle}>
            {entries.map(entry => (
              <div
                key={entry.id}
                className={historyRowClassName}
                style={historyRowStyle}
              >
                {entry.collectedAt ? (
                  <Text size="caption">
                    {formatCollectionDate(entry.collectedAt)}
                  </Text>
                ) : null}
                {entry.value ? (
                  <Text
                    size="caption"
                    style={{ color: theme.palette.primary.main }}
                  >
                    {entry.value}
                  </Text>
                ) : null}
              </div>
            ))}
          </div>
          <div style={braceLabelStyle}>
            <span style={braceSlotStyle} aria-hidden>
              <svg
                viewBox="0 0 10 100"
                preserveAspectRatio="none"
                style={braceStyle}
              >
                <path
                  d="M4 1H9V99H4"
                  fill="none"
                  stroke={theme.palette.text.secondary}
                  strokeWidth="1.5"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            </span>
            <Text
              size="caption"
              style={{ color: theme.palette.text.secondary }}
            >
              {formatMessage(messages.historicalValues)}
            </Text>
          </div>
        </div>
      ) : null}
      {failed && entries.length > 0 ? (
        <Text size="caption">{formatMessage(messages.error)}</Text>
      ) : null}
      {hasMore ? (
        <button
          type="button"
          style={{ ...loadMoreStyle, color: theme.palette.primary.main }}
          disabled={isLoading}
          onClick={event => {
            event.stopPropagation();
            onLoadMore();
          }}
          {...getTestProps('member-extraction-load-more')}
        >
          {formatMessage(messages.loadMore)}
        </button>
      ) : null}
    </div>
  );
};
